import React, { useMemo, useState } from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import { MRT_ColumnDef, MRT_ColumnFiltersState, MRT_SortingState } from "material-react-table";
import { format } from "date-fns";

import { DefaultPage } from "../../layouts/default-page";
import { DataTableFive } from "../../layouts/layout2/data-table-five";
import { Notify } from "../../layouts/notify";

import { useGetMoviePostersQuery } from "../../services/movies.query";

type MoviePosterRow = {
  id: string | number;
  mtrcb_application_no?: string | null;
  title?: string | null;
  pm_description?: string | null;
  filename?: string | null;
  date_approved_last?: string | null;
};

const ROWS_PER_PAGE = 10;

const getPosterUrl = (filename?: string | null) => {
  return `http://midas.mtrcb.gov.ph/etransact/assets/uploads/pm/${filename || "pm.png"
    }`;
};

export const PostersPage = () => {
  const currentYear = new Date().getFullYear();

  const [selectedYear, setSelectedYear] = useState(currentYear);

  const [page, setPage] = useState(1);

  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: "date_approved_last", desc: true },
  ]);

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);

  const titleFilterValue = (columnFilters.find((f) => f.id === "title")?.value as string) || "";

  const {
    data: moviePosterResponse,
    isLoading,
    isFetching,
    isError,
  } = useGetMoviePostersQuery({
    year: selectedYear,
    title: titleFilterValue,
    page,
    rowsPerPage: ROWS_PER_PAGE,
  });

  const moviePosters = moviePosterResponse?.list ?? [];
  const count = moviePosterResponse?.count ?? 0;

  const yearOptions = useMemo(() => {
    const startYear = 2000;
    const endYear = currentYear + 1;

    return Array.from(
      { length: endYear - startYear + 1 },
      (_, index) => endYear - index
    );
  }, [currentYear]);

  const columns = useMemo<MRT_ColumnDef<MoviePosterRow>[]>(
    () => [
      {
        accessorKey: "mtrcb_application_no",
        header: "APPLICATION NO.",
        size: 180,
      },
      {
        accessorKey: "title",
        header: "TITLE",
        size: 280,
        Cell: ({ row }) => (
          <Typography fontWeight={500}>
            {row.original.title || "-"}
          </Typography>
        ),
      },
      {
        accessorKey: "pm_description",
        header: "DESCRIPTION",
        size: 300,
        Cell: ({ row }) => row.original.pm_description || "-",
      },
      {
        accessorKey: "filename",
        header: "FILENAME",
        size: 260,
        Cell: ({ row }) => row.original.filename || "-",
      },
      {
        accessorKey: "image",
        header: "IMAGE",
        size: 160,
        Cell: ({ row }) => {
          const filename = row.original.filename;
          const posterUrl = getPosterUrl(filename);

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexDirection: "row",
              }}
            >
              <img
                alt={row.original.title || "Movie poster"}
                src={posterUrl}
                style={{
                  width: 42,
                  height: 42,
                  objectFit: "cover",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
                onClick={() => window.open(posterUrl, "_blank")}
              />

              <IconButton
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(filename || "").then(() => {
                    Notify("info", "Filename copied to clipboard");
                  });
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        },
      },
      {
        accessorKey: "date_approved_last",
        header: "RELEASE DATE",
        size: 180,
        Cell: ({ row }) => {
          const value = row.original.date_approved_last;

          if (!value) return "-";

          return format(new Date(value), "MMM dd, yyyy");
        },
      },
    ],
    []
  );

  return (
    <DefaultPage title="Posters">
      <Paper
        elevation={0}

      >
        {/* Header wrapper */}
        <Box display="flex" flexDirection="column" gap={1.5} mb={2}>
          {/* Row 1 */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="bold">
              {/* Empty because DefaultPage already shows the title */}
            </Typography>
          </Box>

          {/* Row 2: Year + Search */}
          <Box display="flex" alignItems="center" gap={1.5}>
            <FormControl
              size="small"
              sx={{
                minWidth: 140,
                "& .MuiOutlinedInput-root": { height: 40 },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  py: 0,
                },
              }}
            >
              <InputLabel id="poster-year-label">Year</InputLabel>
              <Select
                labelId="poster-year-label"
                value={selectedYear}
                label="Year"
                onChange={(e) => {
                  setSelectedYear(Number(e.target.value));
                  setPage(1);
                }}
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box flexGrow={1} />
          </Box>
        </Box>
        <DataTableFive<MoviePosterRow>
          total={count}
          columns={columns}
          data={moviePosters}
          page={page}
          pageSize={ROWS_PER_PAGE}
          onPageChange={setPage}
          getRowId={(row: MoviePosterRow) => String(row.id)}
          isLoading={isLoading || isFetching}
          sorting={sorting}
          onSortingChange={(newSorting) => {
            setSorting(newSorting);
            setPage(1);
          }}
          columnFilters={columnFilters}
          onColumnFiltersChange={(newFilters) => {
            setColumnFilters(newFilters);
            setPage(1);
          }}
        />
      </Paper>
    </DefaultPage>
  );
};